/**
 * Living Project Guide Route
 *
 * SEO-first DIY project template page:
 * 1. Full content rendered server-side for Google indexing
 * 2. Live personalization via Anchor FAB
 * 3. Collapsible sections for clean mobile UX
 * 4. Conversion CTA to sign up and save
 * 5. URL presets for SEO landing variants (e.g., ?heated=true)
 *
 * Phase 5: Implements the Living Project Guide spec.
 * Phase 5.1: Dynamic Template System V2 with sections and URL presets.
 */

import { useEffect } from 'react'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { convexQuery } from '@convex-dev/react-query'
import { useSuspenseQuery } from '@tanstack/react-query'
import { api } from 'convex/_generated/api'
import type { AnswerDelta, ProjectTemplate, UrlPreset } from '@/features/diy'
import { ProjectGuidePage } from '@/features/diy'
import { ContentTierProvider } from '@/features/diy/context'
import { AnchorFAB, FABProvider } from '@/components/AnchorFAB'
import { usePostHog } from '@/lib/posthog'
import { trackTemplatePageViewed } from '@/features/diy/utils/analytics'
import { getSiteUrl } from '@/utils/site-url'

// Loader return type for head function
interface LoaderData {
  template: ProjectTemplate
  deltas: Array<{ questionKey: string; answerValue: string; delta: unknown }>
}

// Find matching URL presets from search params
function findAppliedPresets(
  urlPresets: Array<UrlPreset> | undefined,
  search: Record<string, string>
): Array<UrlPreset> {
  if (!urlPresets || urlPresets.length === 0) return []
  
  return urlPresets.filter(preset => {
    const paramValue = search[preset.urlParam]
    const expectedValue = preset.urlValue ?? 'true'
    return paramValue === expectedValue
  })
}

export const Route = createFileRoute('/diy/$slug')({
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    // Convert all search params to strings for consistent handling
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(search)) {
      if (typeof value === 'string') {
        result[key] = value
      } else if (value !== undefined && value !== null) {
        result[key] = String(value)
      }
    }
    return result
  },
  loader: async ({ context, params }) => {
    // Prefetch template data for SSR
    const template = await context.queryClient.ensureQueryData(
      convexQuery(api.diy.templates.getTemplateBySlug, { slug: params.slug })
    )

    // Return 404 if template doesn't exist or is inactive
    if (!template) {
      throw notFound()
    }

    // Prefetch deltas and related templates in parallel
    const [, deltas] = await Promise.all([
      context.queryClient.ensureQueryData(
        convexQuery(api.diy.templates.getRelatedTemplates, {
          templateId: template._id,
          category: template.category,
          difficulty: template.difficulty,
          limit: 4,
        })
      ),
      context.queryClient.ensureQueryData(
        convexQuery(api.ai.deltaQueries.getDeltasForTemplate, {
          templateId: template._id,
        })
      ),
    ])

    return { template, deltas }
  },
  component: DiyTemplatePage,
  head: ({ loaderData, match }) => {
    const data = loaderData as LoaderData | undefined
    if (!data?.template) return {}
    const { template } = data

    const BASE_URL = getSiteUrl()
    const DEFAULT_OG_IMAGE = `${BASE_URL}/images/og-default.jpg`
    
    // Get search params from match context
    const searchParams = match.search
    
    // Find applied URL presets for SEO overrides
    const appliedPresets = findAppliedPresets(
      template.urlPresets,
      searchParams
    )
    
    // Get SEO overrides from first matching preset
    const titleSuffix = appliedPresets.find(p => p.titleSuffix)?.titleSuffix
    const metaDescriptionOverride = appliedPresets.find(p => p.metaDescriptionOverride)?.metaDescriptionOverride
    const canonicalOverride = appliedPresets.find(p => p.canonicalOverride)?.canonicalOverride

    // Build title with optional suffix
    const baseTitle = template.title
    const title = titleSuffix 
      ? `${baseTitle}${titleSuffix} - Step-by-Step DIY Guide | Anchor`
      : `${baseTitle} - Step-by-Step DIY Guide | Anchor`
    
    // Use override or default description
    const description = metaDescriptionOverride ??
      template.metaDescription ??
      `Complete guide to ${template.title.toLowerCase()}. Includes step-by-step instructions, tools list, cost estimates, and expert tips.`
    
    // Use override or default canonical
    const canonicalSlug = canonicalOverride ?? template.canonicalSlug ?? template.slug
    const canonicalUrl = `${BASE_URL}/diy/${canonicalSlug}`
    const ogImage = template.heroImageUrl ?? DEFAULT_OG_IMAGE

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        // Open Graph
        { property: 'og:title', content: titleSuffix ? `${baseTitle}${titleSuffix}` : baseTitle },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'article' },
        { property: 'og:url', content: canonicalUrl },
        { property: 'og:image', content: ogImage },
        // Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: titleSuffix ? `${baseTitle}${titleSuffix}` : baseTitle },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage },
      ],
      links: [{ rel: 'canonical', href: canonicalUrl }],
    }
  },
})

function DiyTemplatePage() {
  const { slug } = Route.useParams()
  const search = Route.useSearch()
  const posthog = usePostHog()

  // Use suspense query for reactive data
  const { data: template } = useSuspenseQuery(
    convexQuery(api.diy.templates.getTemplateBySlug, { slug })
  )

  // Loader already checked this, but TypeScript needs the guard
  if (!template) {
    throw notFound()
  }

  // Fetch related projects and deltas (suspense ensures SSR/hydration consistency)
  const { data: relatedProjects } = useSuspenseQuery(
    convexQuery(api.diy.templates.getRelatedTemplates, {
      templateId: template._id,
      category: template.category,
      difficulty: template.difficulty,
      limit: 4,
    })
  )
  
  const { data: deltasData } = useSuspenseQuery(
    convexQuery(api.ai.deltaQueries.getDeltasForTemplate, {
      templateId: template._id,
    })
  )
  
  // Transform deltas to frontend format
  // Note: The delta shape from DB matches AnswerDelta but TypeScript needs the cast
  // due to subtle differences in optional field handling
  const deltas = deltasData.map(d => ({
    questionKey: d.questionKey,
    answerValue: d.answerValue,
    delta: d.delta as AnswerDelta['delta'],
  })) as Array<AnswerDelta>

  useEffect(() => {
    trackTemplatePageViewed(posthog, {
      projectSlug: slug,
      category: template.category,
      difficulty: template.difficulty,
      referrer:
        typeof document !== 'undefined' ? document.referrer || undefined : undefined,
      utm_source: search.utm_source,
      utm_medium: search.utm_medium,
      utm_campaign: search.utm_campaign,
      utm_content: search.utm_content,
      utm_term: search.utm_term,
      searchParamKeys: Object.keys(search),
    })
  }, [posthog, slug, template.category, template.difficulty, search])

  // Wrap in FABProvider and ContentTierProvider since this is a public route
  return (
    <FABProvider>
      <ContentTierProvider>
        <ProjectGuidePage 
          template={template} 
          relatedProjects={relatedProjects}
          deltas={deltas}
        />
        <AnchorFAB />
      </ContentTierProvider>
    </FABProvider>
  )
}
